import type { CaseRecord, QueueKey } from '@/shared/domain/case';
import type { MailRecord } from '@/shared/domain/intake';

/** Live counts behind the sidebar badges. Every figure is derived from the case
    and mail records, so a badge can never drift from the table it points at. */
export interface NavCounters {
  readonly intake: number;
  readonly work: number;
  readonly mine: number;
  readonly unassigned: number;
  readonly awaiting: number;
  readonly attention: number;
  readonly macd: number;
  readonly catalog: number;
  readonly 'non-order': number;
  readonly parked: number;
}

export function byQueue(records: readonly CaseRecord[], queue: QueueKey): readonly CaseRecord[] {
  return records.filter((record) => record.queue === queue);
}

export function findCase(records: readonly CaseRecord[], id: string): CaseRecord | undefined {
  return records.find((record) => record.id === id);
}

export function isMine(record: CaseRecord, userName: string): boolean {
  return record.owner === userName;
}

export function isUnassigned(record: CaseRecord): boolean {
  return record.owner === null;
}

/** Waiting on the requester rather than on us.
 *
 *  Compared against the key, not the label. `'Requester'` is what the column
 *  says in English and nothing at all in German, so a comparison against the
 *  word would have made this counter read zero the moment the cockpit switched
 *  language — silently, since a queue with nothing awaiting a reply is a
 *  perfectly ordinary state of affairs. */
export function isAwaitingReply(record: CaseRecord): boolean {
  return record.deskKey === 'requester';
}

/** Flagged in the table, or below the confidence floor an Order Manager trusts. */
export function needsAttention(record: CaseRecord): boolean {
  return record.attention !== undefined || record.confidence < 0.5;
}

export function hasConflict(record: CaseRecord): boolean {
  return record.disposition === 'conflict';
}

/* How long a case has been waiting, in hours.
 *
 * `age` is written the way a person says it — "6h", "3d" — and that reads well in
 * a cell but sorts alphabetically into nonsense: 1d before 2h before 3d. A queue
 * exists to answer "what has waited longest", so the column carries the number
 * and the unit stays in the header. Anything unparseable sorts as brand new
 * rather than as ancient, so a typo cannot push a case to the top of the desk. */
export function ageHours(age: string): number {
  const match = /^(\d+)\s*([hdw])$/i.exec(age.trim());
  if (match === null) return 0;

  const size = Number(match[1]);
  switch (match[2]?.toLowerCase()) {
    case 'd':
      return size * 24;
    case 'w':
      return size * 24 * 7;
    default:
      return size;
  }
}

export function countNav(
  records: readonly CaseRecord[],
  mailRecords: readonly MailRecord[],
  userName: string,
): NavCounters {
  const work = byQueue(records, 'work');
  return {
    intake: mailRecords.length,
    work: work.length,
    mine: work.filter((record) => isMine(record, userName)).length,
    unassigned: work.filter(isUnassigned).length,
    awaiting: work.filter(isAwaitingReply).length,
    attention: work.filter(needsAttention).length,
    macd: byQueue(records, 'macd').length,
    catalog: byQueue(records, 'catalog').length,
    'non-order': byQueue(records, 'non-order').length,
    parked: byQueue(records, 'parked').length,
  };
}
